import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { useTranslation } from '@frontend/i18n';
import { humanizeDateTime } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, NakedButton } from '@frontend/design-system';
import { useKeywordSearchStore } from '../../../providers/keyword-search.provider/keyword-search.provider';
import { useTeamChatSelector } from '../../../providers/team-chat.provider';
import { getUserFullName } from '../../../utils';
import { ChatAvatar } from '../../common/chat-avatar/chat-avatar';
import { Attachments } from '../../conversation/message/attachments';
import { MessageBodyText } from '../../conversation/message/message-body-text';

interface KeywordListItemProps {
  message: TeamChatTypes.Message;
}

export const KeywordListItem: FC<KeywordListItemProps> = ({ message }) => {
  const { t } = useTranslation('team-chat');
  const { keyword } = useKeywordSearchStore(['keyword']);
  const openStaticConversation = useTeamChatSelector((ctx) => ctx.openStaticConversation);
  const openThread = useTeamChatSelector((ctx) => ctx.openThread);
  const senderUser = useTeamChatSelector((ctx) => ctx.helpers.getUser(message.userId));
  const channel = useTeamChatSelector((ctx) => ctx.helpers.getConversation(message.channelId));

  const name = getUserFullName(senderUser?.firstName ?? '', senderUser?.lastName ?? '');

  const handleClick = useCallback(() => {
    if (message.parentId) {
      openThread(message.parentId, message.channelId, message.parentId);
    } else {
      openStaticConversation(message.channelId, message.id);
    }
  }, [message.id, message.parentId, message.channelId, openStaticConversation, openThread]);

  return (
    <NakedButton key={message.id} css={styles.list} onClick={handleClick}>
      {channel?.name && (
        <Text css={styles.headerText} textAlign='left' size='medium'>
          {channel?.type === 'DM' ? t('In Direct Message ') : t('#')}
          {channel.name}
        </Text>
      )}
      <div css={styles.messageContainer}>
        {senderUser && <ChatAvatar users={[senderUser]} size='small' shouldShowOnlineStatus={false} />}
        <div>
          <div css={styles.header}>
            <Text weight='bold'>{name || 'Unknown Member'}</Text>
            <Text as='time' size='small' color='subdued'>
              {humanizeDateTime(message.createdAt)}
            </Text>
          </div>
          <MessageBodyText message={message.text} mentionedUsers={message.mentionedUsers} highlightedText={keyword} />
          {!!message?.attachments?.length && <Attachments attachments={message.attachments.map((a) => a.url)} />}
        </div>
      </div>
    </NakedButton>
  );
};

const styles = {
  list: css`
    gap: ${theme.spacing(1)};
    border-bottom: 0.5px solid ${theme.colors.neutral20};
    list-style: none;
    cursor: pointer;
    width: 100%;
    &:hover {
      background: ${theme.colors.neutral5};
    }
  `,
  messageContainer: css`
    display: flex;
    gap: ${theme.spacing(1)};
    flex-direction: row;
    padding: ${theme.spacing(1, 2)};
  `,
  headerText: css`
    color: ${theme.colors.neutral70};
    padding: ${theme.spacing(1, 2)};
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
};
